import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bot, UserCircle, Sparkles, AlertTriangle } from "lucide-react";

export const ResolverSelector = ({ onSelect, forceManual = false }) => {
  const [selected, setSelected] = useState(forceManual ? "manual" : null);

  useEffect(() => {
    if (forceManual) {
      setSelected("manual");
    }
  }, [forceManual]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (forceManual) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        setSelected((prev) => (prev === "ai" ? "manual" : "ai"));
      }
      if (e.key === "Enter" && selected !== null) {
        e.preventDefault();
        onSelect(selected);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selected, onSelect, forceManual]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="gm-resolver"
    >
      <div className="gm-resolver-row">
        <motion.button
          whileTap={forceManual ? {} : { scale: 0.96 }}
          onClick={() => !forceManual && onSelect("ai")}
          disabled={forceManual}
          className={`gm-resolver-btn ${
            forceManual
              ? "gm-resolver-btn--disabled"
              : selected === "ai"
                ? "gm-resolver-btn--active"
                : ""
          }`}
        >
          <div
            className={`gm-resolver-icon ${forceManual ? "gm-resolver-icon--muted" : "gm-resolver-icon--primary"}`}
          >
<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
  <path d="M11 7.33341V3.66675H7.33331" stroke="#DAD9D9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M16.5 7.33325H5.50002C4.4875 7.33325 3.66669 8.15406 3.66669 9.16659V16.4999C3.66669 17.5124 4.4875 18.3333 5.50002 18.3333H16.5C17.5125 18.3333 18.3334 17.5124 18.3334 16.4999V9.16659C18.3334 8.15406 17.5125 7.33325 16.5 7.33325Z" stroke="#DAD9D9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M1.83331 12.8333H3.66665" stroke="#DAD9D9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M18.3333 12.8333H20.1666" stroke="#DAD9D9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M13.75 11.9167V13.7501" stroke="#DAD9D9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.25 11.9167V13.7501" stroke="#DAD9D9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
          </div>
          <div>
            <div className="gm-resolver-label-row">
              <h3 className="gm-resolver-title">AI Auto</h3>
            </div>
            <p className="gm-resolver-desc">Verified data</p>
          </div>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={() => onSelect("manual")}
          className={`gm-resolver-btn ${
            selected === "manual" || forceManual
              ? "gm-resolver-btn--active"
              : ""
          }`}
        >
          <div className="gm-resolver-icon gm-resolver-icon--secondary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
            >
              <path
                d="M17.4166 19.25V17.4167C17.4166 16.4442 17.0303 15.5116 16.3427 14.8239C15.6551 14.1363 14.7224 13.75 13.75 13.75H8.24998C7.27752 13.75 6.34489 14.1363 5.65725 14.8239C4.96962 15.5116 4.58331 16.4442 4.58331 17.4167V19.25"
                stroke="#DAD9D9"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M11 10.0833C13.025 10.0833 14.6666 8.44171 14.6666 6.41667C14.6666 4.39162 13.025 2.75 11 2.75C8.97494 2.75 7.33331 4.39162 7.33331 6.41667C7.33331 8.44171 8.97494 10.0833 11 10.0833Z"
                stroke="#DAD9D9"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div>
            <h3 className="gm-resolver-title">Manual</h3>
            <p className="gm-resolver-desc">You decide</p>
          </div>
        </motion.button>
      </div>

      {forceManual && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="gm-resolver-warning"
        >
          <AlertTriangle className="gm-icon-sm gm-text-amber" />
          <p className="gm-resolver-warning-text">
            AI resolution is unavailable due to the structure of this question.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};
